export type NoteType = "text" | "secure" | "location" | "finance";

export type FinanceDirection = "expense" | "income";

export interface NoteLocation {
  lat: number;
  lng: number;
  label?: string;
}

export interface NoteEntry {
  id: string;
  type: NoteType;
  createdAt: string;
  updatedAt?: string;
  content?: string; // text: content; secure: only present when newly added/edited (plaintext); finance: optional description
  hasContent?: boolean; // secure: server flag indicating an encrypted value exists
  location?: NoteLocation;
  amount?: number; // finance
  currency?: string; // finance
  direction?: FinanceDirection; // finance
  date?: string; // finance: optional user-provided date (YYYY-MM-DD), supports back-dating
  _editing?: boolean; // client-only transient flag
}

export const genNoteId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/** Normalise a raw notes value (legacy string or array) into NoteEntry[]. */
export const coerceNotes = (raw: any): NoteEntry[] => {
  if (Array.isArray(raw)) return raw as NoteEntry[];
  if (typeof raw === "string" && raw.trim()) {
    return [
      {
        id: genNoteId(),
        type: "text",
        createdAt: new Date().toISOString(),
        content: raw,
      },
    ];
  }
  return [];
};

/** Strip client-only fields before persisting. */
export const cleanNotesForSave = (notes: NoteEntry[]): NoteEntry[] =>
  notes.map(({ _editing, hasContent, ...rest }) => {
    // For locked secure notes (no plaintext in memory), keep hasContent so we
    // don't accidentally treat them as empty on the client; backend ignores it.
    if (rest.type === "secure" && !rest.content) {
      return { ...rest, hasContent: true } as NoteEntry;
    }
    return rest as NoteEntry;
  });
